import { StatusCodes } from 'http-status-codes'

import { apiClient } from '@/api/api-client/ApiClient'
import { config } from '@/api/config'
import { showErrorResponse } from '@/hooks/use-show-error-response'
import { logApiEvent, sanitizeData, isErrorLogged } from '@/utils/sentry-logger'

function getBody(data, options) {
  const contentType = options.headers['Content-Type']
  return contentType === 'application/json' ? JSON.stringify(data) : data
}

export const fetcher = async ({ url, data, options }) => {
  options = { ...config, ...options }
  const requestBody = getBody(data, options)
  const requestStartTime = Date.now()

  try {
    const response = await apiClient.fetch(url, { ...options, body: requestBody })
    if (!response.ok) {
      let errorPayload = null
      try {
        // Try to read JSON error body, but don't fail if it's empty/invalid
        errorPayload = await response.json()
      } catch (e) {
        errorPayload = null
      }

      const message =
        (errorPayload && (errorPayload.message || errorPayload.error?.message)) ||
        `Request failed with status ${response.status}`

      const err = new Error(message)
      err.response = response
      err.payload = errorPayload

      logApiEvent(
        url,
        options?.method || 'GET',
        {
          errorMessage: message,
          backendException: {
            type: errorPayload?.exception || null,
            file: errorPayload?.file || null,
            line: errorPayload?.line || null,
            message: errorPayload?.message || null,
            trace: errorPayload?.trace?.slice?.(0, 5) || null,
          },
          requestPayload: sanitizeData(data),
          serverResponse: errorPayload,
          statusCode: response.status,
          statusText: response.statusText,
          responseTime: Date.now() - requestStartTime,
          timestamp: new Date().toISOString(),
        },
        'failure'
      )

      throw err
    }
    if (response.status === StatusCodes.NO_CONTENT) {
      logApiEvent(
        url,
        options?.method || 'GET',
        {
          requestPayload: sanitizeData(data),
          statusCode: response.status,
          statusText: response.statusText,
          responseTime: Date.now() - requestStartTime,
          timestamp: new Date().toISOString(),
        },
        'success'
      )
      return { succes: true }
    }

    const responseData = await response.json()

    // Log successful requests with payload and response
    logApiEvent(
      url,
      options?.method || 'GET',
      {
        requestPayload: sanitizeData(data),
        serverResponse: sanitizeData(responseData),
        statusCode: response.status,
        statusText: response.statusText,
        responseTime: Date.now() - requestStartTime,
        timestamp: new Date().toISOString(),
      },
      'success'
    )

    return responseData
  } catch (error) {
    showErrorResponse(error)

    if (error instanceof Error && !error.response && !isErrorLogged(error)) {
      // Network error or unexpected error (not already logged above)
      logApiEvent(
        url,
        options?.method || 'GET',
        {
          errorMessage: error.message || 'Network or unexpected error',
          errorName: error.name,
          errorStack: error.stack?.substring?.(0, 500),
          requestPayload: sanitizeData(data),
          statusCode: error.status || error.code,
          responseTime: Date.now() - requestStartTime,
          timestamp: new Date().toISOString(),
        },
        'failure'
      )
    }

    if (error instanceof Error) {
      // Already a proper Error (from above or from apiClient/fetch)
      throw error
    }

    const fallbackMessage =
      typeof error === 'string'
        ? error
        : (error && typeof error.message === 'string' && error.message) || 'Unexpected error during request'

    const wrappedError = new Error(fallbackMessage)
    wrappedError.originalError = error
    wrappedError.requestBody = requestBody
    throw wrappedError
  }
}
